class MRAppointmentSlider extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-mr-slider-track]');
    this.slides = [...this.querySelectorAll('[data-mr-slide]')];
    if (!this.track || !this.slides.length) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    this.track.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !this.finePointer.matches || this.positions?.length < 2) return;
      const direction = this.direction(event.clientX);
      if (this.track.dataset.mrDirection !== direction) this.track.dataset.mrDirection = direction;
    }, options);
    this.track.addEventListener('pointerleave', () => delete this.track.dataset.mrDirection, options);
    this.track.addEventListener('pointerdown', (event) => {
      this.pointerStart = { x: event.clientX, y: event.clientY };
      if (event.pointerType !== 'mouse') this.stop();
    }, options);
    this.track.addEventListener('wheel', () => this.stop(), { ...options, passive: true });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.track.addEventListener('click', (event) => {
      if (!this.finePointer.matches || event.detail === 0 || !this.positions || this.positions.length < 2) return;
      if (this.pointerStart && (Math.abs(event.clientX - this.pointerStart.x) > 8 || Math.abs(event.clientY - this.pointerStart.y) > 8)) return;
      const direction = this.direction(event.clientX);
      this.track.dataset.mrDirection = direction;
      this.go((this.targetIndex ?? this.current) + (direction === 'next' ? 1 : -1));
    }, options);
    this.track.addEventListener('scroll', () => this.sync(), options);
    this.track.addEventListener('keydown', (event) => {
      if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'Home') this.go(0);
        else if (event.key === 'End') this.go(this.positions.length - 1);
        else this.go((this.targetIndex ?? this.current) + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, options);
    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.track);
    this.layout();
  }
  direction(clientX) {
    const bounds = this.track.getBoundingClientRect();
    return clientX - bounds.left < bounds.width / 2 ? 'previous' : 'next';
  }
  disconnectedCallback() {
    this.stop();
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
  }
  layout() {
    if (!this.track.clientWidth) return;
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const visible = Math.max(1, Math.floor((this.track.clientWidth + gap) / (this.slides[0].getBoundingClientRect().width + gap) + 0.01));
    const positions = [0];
    for (let index = visible; index < this.slides.length; index += visible) {
      const offset = Math.min(maximum, this.slides[index].offsetLeft - this.slides[0].offsetLeft);
      if (offset > positions[positions.length - 1] + 1) positions.push(offset);
    }
    if (maximum > positions[positions.length - 1] + 1) positions.push(maximum);
    this.positions = positions;
    if (positions.length < 2) delete this.track.dataset.mrDirection;
    this.sync();
  }
  sync() {
    if (!this.positions) return;
    let closest = 0;
    this.positions.forEach((position, index) => {
      if (Math.abs(this.track.scrollLeft - position) < Math.abs(this.track.scrollLeft - this.positions[closest])) closest = index;
    });
    this.current = closest;
  }
  go(index) {
    if (!this.positions?.length) return;
    const bounded = (index + this.positions.length) % this.positions.length;
    this.stop();
    const start = this.track.scrollLeft;
    const end = this.positions[bounded];
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !this.finePointer.matches) {
      this.track.scrollTo({ left: end, behavior: 'auto' });
      this.sync();
      return;
    }
    this.targetIndex = bounded;
    const started = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - started) / 360);
      const eased = progress * progress * (3 - 2 * progress);
      this.track.scrollLeft = start + (end - start) * eased;
      if (progress < 1) this.animation = requestAnimationFrame(step);
      else {
        this.animation = null;
        this.targetIndex = null;
        this.sync();
      }
    };
    this.animation = requestAnimationFrame(step);
  }
  stop() {
    cancelAnimationFrame(this.animation);
    this.animation = null;
    this.targetIndex = null;
  }
  selectBlock(id) {
    this.stop();
    const slide = this.slides.find((item) => item.dataset.blockId === id);
    if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'auto' });
  }
}
if (!customElements.get('mr-appointment-slider')) customElements.define('mr-appointment-slider', MRAppointmentSlider);


